import 'twin.macro'

import { yupResolver } from '@hookform/resolvers/yup'
import { YMapsApi } from '@pbe/react-yandex-maps/typings/util/typing'
import CameraIcon from 'assets/icons/camera.svg?component'
import DeliveryIcon from 'assets/icons/delivery.svg?component'
import AsyncSelect from 'components/UI/AsyncSelect'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import Button from 'components/UI/Buttons/Button'
import Input from 'components/UI/Inputs/Input'
import Select from 'components/UI/Select'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import { useAppSelector } from 'hooks'
import _debounce from 'lodash/debounce'
import { Fragment, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { SingleValue } from 'react-select'
import { toast } from 'react-toastify'
import { useSetCardDeliveryAddressMutation } from 'redux/api/creditApi'
import { useGetCitiesQuery } from 'redux/api/leadsApi'
import { getActiveCreditApplication } from 'redux/store/slices/userSlice'
import type { CityUnit } from 'share/types/api/leads'
import tw from 'twin.macro'
import * as yup from 'yup'

import { checkIsDeliveryAvailable, defaultMapState, geocodeValue, getAddressObject, YandexMapState } from './helper'
import { YandexMap } from './YandexMap'

type FormSchemaValues = {
  city?: CityUnit
  address?: SuggestOption
  flat: string
  entrance: string
  street?: string
  building: string
  lat?: number
  lon?: number
}

export type SuggestOption = {
  label: string
  value: string
}

type SuggestOptions = Array<SuggestOption>

const formSchema = yup
  .object({
    city: yup
      .object()
      .shape({
        id: yup.string().required(),
        name: yup.string().required(),
        has_delivery: yup.boolean().required()
      })
      .required(), // Город
    address: yup
      .object()
      .shape({
        label: yup.string().required(),
        value: yup.string().required()
      })
      .required(),
    flat: yup.string().required(), // Квартира
    entrance: yup.string().notRequired(), // Подъезд
    street: yup.string().required(), // Улица
    building: yup.string().required(), // Дом / здание
    lat: yup.number().required(), // Широта
    lon: yup.number().required() // Долгота
  })
  .required('Заполните это поле, пожалуйста')

const formDefaultValues = {
  entrance: '',
  flat: '',
  building: '',
  street: '',
  city: undefined,
  address: undefined,
  lon: undefined,
  lat: undefined
}

const fetchSuggestedCities = (
  inputValue: string,
  cb: any,
  onResult: (options: Array<SuggestOption>) => void,
  yMapsApi?: YMapsApi,
  selectedCity?: CityUnit
) => {
  if (!yMapsApi) return cb([])
  const searchAddress = `${selectedCity ? `${selectedCity.name}, ` : ''}${inputValue}`
  yMapsApi.suggest(searchAddress, { results: 5 }).then(data => {
    const normalizedRes = data.map(res => ({
      label: res.displayName,
      value: res.value
    }))
    onResult(normalizedRes)
    cb(normalizedRes)
  })
}

const debouncedFetchSuggestedCities = _debounce(fetchSuggestedCities, 4000)

const LoanCorpCardOpening = ({
  isLoading: isDecisionLoading,
  onSetDecision
}: {
  isLoading: boolean
  onSetDecision: () => void
}) => {
  // Tools
  const {
    t,
    i18n: { language }
  } = useTranslation()

  // State
  const [step, setStep] = useState(1)
  const [mapState, setMapState] = useState<YandexMapState>(defaultMapState)
  const [yMapsApi, setYMapsApi] = useState<YMapsApi | undefined>(undefined)
  const [suggestResults, setSuggestResults] = useState<SuggestOptions | undefined>([])

  // Selectors
  const { id: creditID } = useAppSelector(getActiveCreditApplication)

  // Queries
  const { data: cities, isLoading: isCitiesLoading } = useGetCitiesQuery({
    filterByHasDelivery: true
  })

  // Mutations
  const [setCardDeliveryAddress, { isLoading: isSetAddressLoading }] = useSetCardDeliveryAddressMutation()

  // Form
  const {
    control,
    formState: { isValid },
    setValue: setFormValue,
    reset,
    watch,
    handleSubmit
  } = useForm<FormSchemaValues>({
    defaultValues: formDefaultValues,
    resolver: yupResolver(formSchema)
  })

  const selectedCity = watch('city')

  // Handlers
  const setCoordsAndPlacemark = async (value: string | number[], yMaps = yMapsApi) => {
    if (!yMaps || !value) return
    const { coords, firstGeoObject, response } = await geocodeValue(yMaps, value, {
      results: 1
    })
    setMapState(prevState => ({
      ...prevState,
      center: coords,
      markerCoords: coords
    }))
    return {
      coords,
      firstGeoObject,
      response
    }
  }

  const geocodeSuggestedValue = async (suggestValue: SingleValue<SuggestOption | undefined>) => {
    if (!suggestValue) return
    try {
      return await setCoordsAndPlacemark(suggestValue.value)
    } catch (error) {
      console.log(error)
    }
  }

  const handleCityChange = async (city: SingleValue<CityUnit>) => {
    if (!city) return
    try {
      if (step !== 2) return setStep(2)
      reset(values => ({ ...formDefaultValues, city: values.city }))
      setSuggestResults(() => [])
      await setCoordsAndPlacemark(city.name)
    } catch (error) {
      console.log(error)
    }
  }

  const validateGeoObjectAndSetFormValue = (geoObject: any) => {
    if (!cities) return

    // Reset form whenever user changes address city or click
    reset(values => ({ ...formDefaultValues, city: values.city, address: values.address }))

    const { components, coords } = getAddressObject(geoObject)
    const foundedCity = cities.find(city => city.name === (components.locality || components.province))

    if (!checkIsDeliveryAvailable(cities, foundedCity?.name) || !foundedCity) {
      return toast.error(t('no-city-card'), {
        position: 'bottom-left',
        toastId: 'notifications-toast'
      })
    }
    if (!components.house) {
      return toast.error(t('select-on-map'), {
        position: 'bottom-left',
        toastId: 'notifications-toast'
      })
    }

    // Remove toast from screen if validation passed OK!
    toast.dismiss()

    const addressProperties = geoObject.properties.getAll()
    setFormValue('city', foundedCity)
    setFormValue('address', {
      label: addressProperties.name,
      value: addressProperties.text
    })
    setFormValue('street', components.street ?? components.district)
    setFormValue('building', components.house)
    setFormValue('lat', coords[0])
    setFormValue('lon', coords[1])
  }

  const handleAddressChange = async (value: SingleValue<SuggestOption>) => {
    if (!value) return
    try {
      const res = await geocodeSuggestedValue(value)
      if (!res) return
      validateGeoObjectAndSetFormValue(res.firstGeoObject)
    } catch (error) {
      console.log(error)
    }
  }

  const handleGeocoderValueChange = (geoObject: any) => {
    if (!geoObject) return
    validateGeoObjectAndSetFormValue(geoObject)
  }

  const handleMapStateChange = (value: Partial<YandexMapState>) => {
    setMapState(prevState => ({
      ...prevState,
      ...value
    }))
  }

  const onSubmit = async (data: FormSchemaValues) => {
    try {
      if (!creditID || !data.street || !data.lat || !data.lon || !data.address || !data.city) return
      await setCardDeliveryAddress({
        application: creditID,
        card_type: 'CORPORATE_CARD',
        delivery_address: {
          city: data.city.name,
          building: data.building,
          entrance: data.entrance,
          flat: data.flat,
          street: data.street,
          lat: data.lat,
          lon: data.lon,
          address_str: data.address.value
        }
      }).unwrap()
      onSetDecision()
    } catch (err) {
      toast.error(t('delivery-fields-error'), {
        position: 'bottom-left',
        toastId: 'notifications-toast'
      })
    }
  }

  const onSkip = async () => {
    try {
      if (!creditID) return
      await setCardDeliveryAddress({
        application: creditID,
        card_type: 'CORPORATE_CARD',
        skip_corp_card_creation: true
      }).unwrap()
      onSetDecision()
    } catch (error) {
      console.log(error)
      return toast.error(t('delivery-fields-error'), {
        position: 'bottom-left',
        toastId: 'notifications-toast'
      })
    }
  }

  const onYMapsReady = async (yMapsApi: YMapsApi) => {
    setYMapsApi(yMapsApi)
    if (!selectedCity) return
    await setCoordsAndPlacemark(selectedCity.name, yMapsApi)
  }

  return (
    <Fragment>
      <div tw='px-4 md:px-0 mt-4 md:mt-0'>
        <Heading variant='title2'>{t('corp-card-title')}</Heading>
        <Typography variant='subbody' tw='mt-2 text-grey-dark'>
          {t('corp-card-text-1')}
        </Typography>
        <Typography variant='subbody' tw='mt-1 text-grey-dark'>
          {t('corp-card-text-2')}
        </Typography>
        <Typography variant='subbody' tw='mt-1 text-grey-dark'>
          <Trans
            components={{
              a: (
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a
                  tw='underline text-main'
                  href='https://bankffin.kz/storage/files/docs/261/doc-ru-261-1672205237.PDF'
                  target='_blank'
                  rel='noreferrer'
                />
              )
            }}
          >
            {t('corp-card-link-text')}
          </Trans>
        </Typography>
      </div>
      <InfoBlock twStyle={tw`flex flex-col gap-5 mt-8`}>
        <Heading tw='text-center' variant='subtitle'>
          {t('cities-ph')}
        </Heading>
        <Select
          control={control}
          options={cities}
          name='city'
          placeholder={t('city')}
          label={null}
          isSearchable
          isDisabled={isCitiesLoading || isSetAddressLoading || isDecisionLoading}
          noOptionsMessage={() => t('no-data')}
          getOptionLabel={option => (language === 'ru' ? option.name : option.name_kk)}
          getOptionValue={option => option.id}
          onChange={handleCityChange}
        />
        <Typography variant='caption' twStyle={tw`text-grey-dark -mt-2`}>
          {t('corp-card-subtitle')}
        </Typography>
      </InfoBlock>
      {step === 2 && (
        <InfoBlock twStyle={tw`flex flex-col gap-5 mt-4`}>
          <Heading tw='text-center' variant='subtitle'>
            {t('enter-adress')}
          </Heading>
          <AsyncSelect
            key={selectedCity?.id}
            control={control}
            isDisabled={isSetAddressLoading || isDecisionLoading}
            placeholder={t('district-street-house')}
            name='address'
            isSearchable
            label={t('choose-adress')}
            defaultOptions={suggestResults}
            onChange={handleAddressChange}
            loadOptions={(inputValue, cb) =>
              debouncedFetchSuggestedCities(inputValue, cb, setSuggestResults, yMapsApi, selectedCity)
            }
            getOptionLabel={option => option.label}
            getOptionValue={option => option.value}
            noOptionsMessage={() => t('flow-not-founded')}
          />
          <YandexMap
            mapState={mapState}
            onMapStateChange={handleMapStateChange}
            onGeocoderValueChange={handleGeocoderValueChange}
            onYandexMapApiReady={onYMapsReady}
          />
          <div tw='grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-[12px]'>
            <Input
              control={control}
              label={t('choose-apart')}
              disabled={isSetAddressLoading || isDecisionLoading}
              placeholder={t('apartment-office') as string}
              name='flat'
            />
            <Input
              control={control}
              label={t('choose-entrance')}
              disabled={isSetAddressLoading || isDecisionLoading}
              placeholder='Подъезд'
              name='entrance'
            />
          </div>
          <div className='space-y-4'>
            <Typography variant='subbody' twStyle={tw`flex items-center gap-x-4`}>
              <CameraIcon tw='flex-shrink-0' />
              <span>
                <Trans components={{ b: <b /> }}>{t('corp-card-conditions.1')}</Trans>
              </span>
            </Typography>
            <Typography variant='subbody' twStyle={tw`flex items-center gap-x-4`}>
              <DeliveryIcon tw='flex-shrink-0' />
              <span>
                <Trans components={{ b: <b /> }}>{t('corp-card-conditions.2')}</Trans>
              </span>
            </Typography>
          </div>
        </InfoBlock>
      )}
      <div tw='px-4 mt-8 flex flex-col items-center gap-x-12 gap-y-4 sm:flex-row md:flex-col lg:flex-row lg:gap-x-4 md:px-0'>
        <Button variant='text' onClick={onSkip}>
          <Typography as='span' weight='bold' variant='subbody'>
            {t('corp-card-skip')}
          </Typography>
        </Button>
        <Button
          variant='primary'
          disabled={!isValid}
          loading={isDecisionLoading || isSetAddressLoading}
          onClick={handleSubmit(onSubmit)}
        >
          <Typography variant='body' weight='bold' twStyle={tw`text-white`}>
            {t('flow-continue')}
          </Typography>
        </Button>
      </div>
    </Fragment>
  )
}

export default LoanCorpCardOpening
